import type { ApiError } from '@tradekaro/shared';

const BASE_URL = (import.meta.env.VITE_API_URL ?? '') as string;

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Fired whenever any API request returns 401. AuthProvider listens for this
 *  to clear the user state, which lets the route guard kick the browser to
 *  /auth/signin instead of silently dropping into demo data. The auth probe
 *  itself (`/auth/me`) is exempt — that one expects 401s for signed-out
 *  visitors and handles them locally without bouncing.
 */
export const UNAUTHORIZED_EVENT = 'tk:unauthorized';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const url = path.startsWith('http') ? path : `${BASE_URL}/api${path}`;
  const res = await fetch(url, {
    method,
    headers: { 'content-type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  if (!res.ok) {
    let payload: ApiError | undefined;
    try {
      payload = (await res.json()) as ApiError;
    } catch {
      // ignore
    }
    if (res.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
    throw new ApiClientError(
      res.status,
      payload?.error ?? 'HTTP_ERROR',
      payload?.message ?? res.statusText,
    );
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
